import React, { createContext, useState, useContext } from 'react';

const CartContext = createContext();

import discountService from '../services/discountService';

export function CartProvider({ children }) {
  const [cart, setCart] = useState(() => {
    try {
      const savedCart = localStorage.getItem('cart');
      return savedCart ? JSON.parse(savedCart) : [];
    } catch (error) {
      console.error('Error loading cart from localStorage:', error);
      return [];
    }
  });
  const [discount, setDiscount] = useState(() => {
    try {
      const savedDiscount = localStorage.getItem('discount');
      return savedDiscount ? JSON.parse(savedDiscount) : null;
    } catch (error) {
      console.error('Error loading discount from localStorage:', error);
      return null;
    }
  });

  React.useEffect(() => {
    try {
      localStorage.setItem('cart', JSON.stringify(cart));
    } catch (error) {
      console.error('Error saving cart to localStorage:', error);
    }
  }, [cart]);

  React.useEffect(() => {
    try {
      if (discount) {
        localStorage.setItem('discount', JSON.stringify(discount));
      } else {
        localStorage.removeItem('discount');
      }
    } catch (error) {
      console.error('Error saving discount to localStorage:', error);
    }
  }, [discount]);

  const addToCart = (product) => {
    setCart(prev => [...prev, { ...product, quantity: 1 }]);
  };

  const removeFromCart = (productId) => {
    setCart(prev => {
      const index = prev.findIndex(item => item.id === productId);
      if (index > -1) {
        const newCart = [...prev];
        newCart.splice(index, 1);
        return newCart;
      }
      return prev;
    });
  };

  // Suppression par RANG et non par identifiant.
  //
  // `removeFromCart` retire la première ligne portant cet id : avec deux
  // boîtes du même pack — le cas normal, puisqu'il n'existe aucun réglage de
  // quantité —, cliquer la croix de la ligne « Fille » supprimait la ligne
  // « Garçon ». Le total restait juste ; c'est la variante conservée qui était
  // la mauvaise, et le vendeur ne pouvait pas le savoir.
  const removeFromCartAt = (itemIndex) => {
    setCart(prev => {
      if (itemIndex < 0 || itemIndex >= prev.length) return prev
      const newCart = [...prev]
      newCart.splice(itemIndex, 1)
      return newCart
    })
  }

  const clearCart = () => {
    setCart([]);
    setDiscount(null);
  };

  const updateCartItemLanguage = (itemIndex, language) => {
    setCart(prev => {
      const newCart = [...prev];
      if (newCart[itemIndex]) {
        newCart[itemIndex] = { ...newCart[itemIndex], language };
      }
      return newCart;
    });
  };

  const applyDiscount = async (code) => {
    try {
      const result = await discountService.validateCoupon(code, subtotal, cart);

      if (!result.valid) {
        throw new Error('Code invalide');
      }

      const discountData = result.discount;

      // Map backend response to context format
      const mappedDiscount = {
        id: discountData.id, // Might be undefined if not returned, but code is main identifier
        code: discountData.code,
        type: discountData.type,
        value: discountData.value,
        amount: discountData.amount // The calculated discount amount from backend
      };

      setDiscount(mappedDiscount);
      return mappedDiscount;
    } catch (error) {
      setDiscount(null);
      throw error;
    }
  };

  const removeDiscount = () => {
    setDiscount(null);
  };

  const subtotal = cart.reduce((sum, item) => sum + (parseFloat(item.price) || 0), 0);

  let discountAmount = 0;
  if (discount) {
    // Always calculate locally based on current subtotal if type and value are available
    if (discount.type && discount.value) {
      if (discount.type === 'percentage') {
        discountAmount = (subtotal * discount.value) / 100;
      } else {
        discountAmount = discount.value;
      }
    } else if (discount.amount !== undefined) {
      // Fallback to backend amount if type/value missing (should not happen with new backend)
      discountAmount = discount.amount;
    }
    // Ensure discount doesn't exceed subtotal
    discountAmount = Math.min(discountAmount, subtotal);
  }

  const total = subtotal - discountAmount;

  return (
    <CartContext.Provider value={{
      cart,
      addToCart,
      removeFromCart,
      removeFromCartAt,
      clearCart,
      updateCartItemLanguage,
      subtotal,
      discount,
      discountAmount,
      total,
      applyDiscount,
      removeDiscount
    }}>
      {children}
    </CartContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useCart() {
  return useContext(CartContext);
}
